import { useState, useEffect, useMemo } from "react";
import { SwitchComponent } from "../../../../../FormComponent";
import SmartInputBox from "../../../shared/SmartInputBox";
import InfoBox from "../../../shared/InfoBox";
import styles from "../../../../../../styles/automation/scriptdetails.module.scss";
import studioStyles from "../../../../../../styles/automationStudio/automationStudio.module.scss";
import AIEvaluationBlock from "../../../shared/AIEvaluationBlock";
import ScreenshotPreview from "../../../shared/ScreenshotPreview";
import StepExecutionStatusRow from "../../../shared/StepExecutionStatusRow";
import { getVerificationTabState } from "../../../../../AutomationStudio/utils/stepExecutionDisplay";
import { LABELS } from "../../../../content";

export default function VerificationTab({
    step,
    onStepChange,
    isExecuting = false,
    fileSuggestions = [],
    fileDescriptions = {},
    variablesList = [],
    variablesListDescriptions = {},
    isReplayFlow = false,
    replayEval = null,
    execDetail = null,
    screenshotRaw = null,
    isDynamicStep = false,
    statusOptions = null,
    instructions = null,
    fieldDirtyMap = null,
}) {
    const format = (t) => t ?? "";
    const suggestions = fileSuggestions;
    // Local controlled state – flush to redux on blur.
    // Display/edit planned eval fields from the same args nodes replay reads.
    const planDescription =
        step?.description ||
        step?.stepDescription ||
        step?.extractedData?.stepDescription ||
        '';
    const execDescription =
        execDetail?.details?.stepDescription ||
        execDetail?.args?.stepDescription ||
        '';
    const rawDescription = planDescription || (isReplayFlow ? execDescription : execDescription);

    const rawExpected =
        step?.args?.expectedOutcome ||
        step?.arguments?.expectedOutcome ||
        '';

    // Replay runs assertion mode from args.assertionCode only — keep UI visibility in sync.
    const rawAssertion =
        step?.args?.assertionCode ||
        step?.arguments?.assertionCode ||
        '';
    const rawRuntimeExpected =
        step?.args?.runTimeExpectedOutcome ||
        step?.arguments?.runTimeExpectedOutcome ||
        '';
    
    const rawActual = isReplayFlow
        ? (
            execDetail?.result?.result?.actualOutcome ??
            execDetail?.result?.actualOutcome ??
            execDetail?.args?.actualOutcome ??
            ''
        )
        : (
            step?.actualOutcome || step?.actual_outcome || step?.args?.actualOutcome ||
            step?.arguments?.actualOutcome || step?.details?.actualOutcome ||
            step?.extractedData?.actualOutcome || step?.extractedData?.evaluation?.actualOutcome ||
            step?.executionGroup?.mainAction?.actualOutcome || step?.executionGroup?.mainAction?.actual_outcome || ''
        );

    const [descValue, setDescValue] = useState(rawDescription);
    const [expectedValue, setExpectedValue] = useState(rawExpected);
    const [assertionValue, setAssertionValue] = useState(rawAssertion);
    const [actualValue, setActualValue] = useState(rawActual);

    useEffect(() => { setDescValue(rawDescription); }, [rawDescription]);
    useEffect(() => { setExpectedValue(rawExpected); }, [rawExpected]);
    useEffect(() => { setAssertionValue(rawAssertion); }, [rawAssertion]);
    useEffect(() => { setActualValue(rawActual); }, [rawActual]);

    const replayDynamicLocked = Boolean(step) && isReplayFlow && isDynamicStep;

    const verificationState = step
        ? getVerificationTabState(step, execDetail, statusOptions)
        : { hasBeenExecuted: false, isPassed: false, isEvaluationTool: false };
    const { hasBeenExecuted, isPassed, isEvaluationTool } = verificationState;

    const rawError = step?.error || step?.details?.error || step?.executionGroup?.mainAction?.error;
    const errorMessage = typeof rawError === 'string' ? rawError : (rawError?.message || rawError);
    const reason = step
        ? (
            (isReplayFlow
                ? (
                    execDetail?.result?.result?.reason ??
                    execDetail?.result?.reason
                )
                : null) ||
            step.reason ||
            step.details?.reason ||
            step.result?.result?.reason ||
            execDetail?.result?.result?.reason ||
            step.args?.reason ||
            step.arguments?.reason ||
            step.failureReason ||
            step.failure_reason ||
            step.executionGroup?.mainAction?.reason ||
            step.executionGroup?.mainAction?.details?.reason ||
            step.executionGroup?.mainAction?.args?.reason ||
            step.executionGroup?.mainAction?.failureReason ||
            step.executionGroup?.mainAction?.failure_reason ||
            errorMessage
        )
        : '';
    const hasReasonError = Boolean(errorMessage) || (hasBeenExecuted && !isPassed);

    const useAssertionCode = step?.useAssertionCode ?? false;
    const showAiEvaluationNarrative = replayDynamicLocked || !useAssertionCode;

    const primaryReplayAi =
        isReplayFlow &&
        replayEval?.isPrimaryAIEvaluation &&
        typeof replayEval?.llmReason === 'string' &&
        replayEval.llmReason.trim().length > 0;
    const aiEvaluationNarrative = primaryReplayAi ? replayEval.llmReason : reason || errorMessage;

    const actualOutcomeDisplay = useMemo(
        () => format(actualValue ?? ''),
        [actualValue, format]
    );
    const aiEvaluationNarrativeDisplay = useMemo(
        () => format(aiEvaluationNarrative),
        [aiEvaluationNarrative, format]
    );
    const failureReasonDisplay = useMemo(
        () => format(reason || errorMessage),
        [reason, errorMessage, format]
    );

    if (!step) return null;

    const confFromReplayPrimary =
        replayEval?.llmConfidence != null
            ? replayEval.llmConfidence === 'high'
                ? 90
                : replayEval.llmConfidence === 'medium'
                  ? 60
                  : 20
            : null;

    // ── Handlers ──────────────────────────────────────────────────────
    const handleAIToggle = (e) => {
        if (replayDynamicLocked) return;
        onStepChange?.({ useAssertionCode: !e.target.checked });
    };

    const handleDescBlur = (e) => {
        if (replayDynamicLocked) return;
        onStepChange?.({ description: e.target.value, stepDescription: e.target.value, extractedData: { ...step.extractedData, stepDescription: e.target.value } });
    };

    const handleExpectedBlur = (e) => {
        if (replayDynamicLocked) return;
        onStepChange?.({
            args: { ...step.args, expectedOutcome: e.target.value },
        });
    };

    const handleAssertionBlur = (e) => {
        if (replayDynamicLocked) return;
        const code = e.target.value;
        onStepChange?.({
            args: { ...step.args, assertionCode: code },
            ...(step.playwrightAssertion
                ? { playwrightAssertion: { ...step.playwrightAssertion, code } }
                : {}),
        });
    };

    const formLocked = isExecuting || replayDynamicLocked;

    return (
        <div className={styles.container}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '16px', width: '100%', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div className={styles.evaluationStepContainer} style={{ flex: '1 1 300px', minWidth: 0 }}>

                {/* <StepExecutionStatusRow  // Intentionaly removed to avoid duplicate status display
                    step={step}
                    isReplayFlow={isReplayFlow}
                    execDetail={execDetail}
                    statusOptions={statusOptions}
                    instructions={instructions}
                /> */}

                {/* ── AI Evaluation toggle ── */}
                <div className={styles.fieldContainerRow}>
                    <span className={styles.title}>AI Evaluation:</span>
                    <span className={styles.value}>
                        <SwitchComponent
                            checked={replayDynamicLocked ? true : !useAssertionCode}
                            onChange={handleAIToggle}
                            size="small"
                            disabled={formLocked}
                        />
                    </span>
                </div>

                {/* ── Step Description ── */}
                <div className={styles.fieldContainer}>
                    <span className={styles.title}>Step Description:</span>
                    <SmartInputBox
                        suggestions={variablesList}
                        descriptions={variablesListDescriptions}
                        fileTrigger="@"
                        fileSuggestions={suggestions}
                        fileDescriptions={fileDescriptions}
                        value={descValue}
                        onChange={(e) => !replayDynamicLocked && setDescValue(e.target.value)}
                        onBlur={handleDescBlur}
                        size="small"
                        disabled={formLocked}
                        readOnly={replayDynamicLocked}
                        placeholder="Describe what this step verifies…"
                        customClassName={fieldDirtyMap?.description ? studioStyles.dirtyField : ''}
                    />
                </div>

                {/* ── Assertion Code (keep visible when empty so user can clear/re-enter) ── */}
                {isEvaluationTool && !replayDynamicLocked && (
                    <div className={styles.fieldContainer}>
                        <span className={styles.title}>Assertion Code:</span>
                        <SmartInputBox
                            suggestions={variablesList}
                            descriptions={variablesListDescriptions}
                            fileTrigger="@"
                            fileSuggestions={suggestions}
                            fileDescriptions={fileDescriptions}
                            disabled={!useAssertionCode || isExecuting}
                            value={assertionValue}
                            onChange={(e) => setAssertionValue(e.target.value)}
                            onBlur={handleAssertionBlur}
                            size="small"
                            placeholder="e.g. await expect(page).toHaveURL('…');"
                            customClassName={fieldDirtyMap?.assertionCode ? studioStyles.dirtyField : ''}
                        />
                    </div>
                )}

                {/* ── Expected / Recorded expected outcome ── */}
                <div className={styles.fieldContainer}>
                    <span className={styles.title}>
                        {replayDynamicLocked ? LABELS.VERIFICATION_RECORDED_EXPECTED_OUTCOME : LABELS.VERIFICATION_EXPECTED_OUTCOME}
                    </span>
                    <SmartInputBox
                        suggestions={variablesList}
                        descriptions={variablesListDescriptions}
                        fileTrigger="@"
                        fileSuggestions={suggestions}
                        fileDescriptions={fileDescriptions}
                        value={expectedValue}
                        onChange={(e) => !replayDynamicLocked && setExpectedValue(e.target.value)}
                        onBlur={handleExpectedBlur}
                        size="small"
                        disabled={formLocked}
                        readOnly={replayDynamicLocked}
                        placeholder="e.g. The page title should be 'Dashboard'"
                        customClassName={fieldDirtyMap?.expectedOutcome ? studioStyles.dirtyField : ''}
                    />
                </div>

                {replayDynamicLocked && (
                    <div className={styles.fieldContainer}>
                        <span className={styles.title}>{LABELS.VERIFICATION_RUNTIME_EXPECTED_OUTCOME}</span>
                        <div className={`${styles.reasonValueBox} ${styles.reasonValueBoxNeutral}`}>
                            {rawRuntimeExpected}
                        </div>
                    </div>
                )}

                {/* ── Actual Outcome – only after this step has run (hide stale plan fields) ── */}
                {hasBeenExecuted && String(actualValue ?? '').trim() !== '' && (
                    <div className={styles.fieldContainer}>
                        <span className={styles.title}>Actual Outcome:</span>
                        <div className={`${styles.reasonValueBox} ${styles.reasonValueBoxNeutral}`}>
                            {actualOutcomeDisplay}
                        </div>
                    </div>
                )}

                {/* ── AI Evaluation narrative: only when AI mode (toggle on); one block, same card as running view ── */}
                {showAiEvaluationNarrative && hasBeenExecuted && isEvaluationTool && aiEvaluationNarrative && (
                    <div className={styles.fieldContainer}>
                        <AIEvaluationBlock
                            reason={String(aiEvaluationNarrativeDisplay)}
                            statusText={isPassed ? 'Passed' : 'Failed'}
                            confidence={primaryReplayAi ? confFromReplayPrimary : step.confidence}
                            stepDuration={primaryReplayAi ? replayEval?.llmDuration : step.duration}
                        />
                    </div>
                )}

                {/* ── Failure reason: assertion-code mode has no AI card — show error box instead ── */}
                {hasBeenExecuted &&
                    !isPassed &&
                    isEvaluationTool &&
                    useAssertionCode &&
                    !replayDynamicLocked &&
                    failureReasonDisplay && (
                        <InfoBox
                            type="error"
                            title="Verification Failed"
                            description={failureReasonDisplay}
                        />
                    )}

                {/* ── Failure reason / error (non-evaluation steps) ── */}
                {hasBeenExecuted && !isPassed && !isEvaluationTool && failureReasonDisplay && (
                    <InfoBox
                        type="error"
                        title="Verification Failed"
                        description={failureReasonDisplay}
                    />
                )}
                </div>
                {hasBeenExecuted && (
                    <ScreenshotPreview
                        screenshotRaw={screenshotRaw}
                        emptyVariant="empty"
                        fileName={descValue || 'Verification step screenshot'}
                    />
                )}
            </div>
        </div>
    );
}
